import { useEffect, useState } from "react";
import { Siren, Newspaper, Radio, Briefcase, FileWarning, Skull, EyeOff, X, Play, Globe, FileSearch } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DEAD_DROP_POS } from "./CityBackdrop";

/* Living-menu DOM layers, composed properly:
   — IntelRail: one anchored left column holding every personal/city card
     (prophecy, mole, absence, my warrant poster, cold case, the Leak, broadcasts)
     instead of scattered floating boxes.
   — WantedDock: a structured police-lineup strip pinned above the tickers.
   — DeadDrop: the briefcase that glows in the backdrop. */

const railCard = "w-full border bg-[#04060a]/95 backdrop-blur-md pointer-events-auto";
const rise = { animation: "ncFadeUp 0.45s cubic-bezier(0.16,1,0.3,1) both" };
const railLabel = "font-mono text-[8px] uppercase tracking-[0.35em]";

/* NCDB most wanted — a pinned police notice board, anchored to the bottom of the
   left column (under the intel rail) so it reads as deliberate set-dressing. */
export function WantedDock({ wanted, accent }) {
  if (!wanted?.length) return null;
  return (
    <div data-testid="wanted-dock" className="absolute left-6 bottom-[104px] z-10 w-72 pointer-events-auto" style={rise}>
      <div className="relative border border-red-500/30 bg-[#04060a]/95 backdrop-blur-md shadow-[0_0_50px_rgba(239,68,68,0.08)]">
        {/* corner ticks — same language as the right rail */}
        <span className="absolute -top-px -left-px w-3 h-3 border-t-2 border-l-2 border-red-500/70" />
        <span className="absolute -bottom-px -right-px w-3 h-3 border-b-2 border-r-2 border-red-500/70" />
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-red-500/20 bg-red-950/25">
          <Siren className="w-3.5 h-3.5 text-red-400 animate-pulse shrink-0" />
          <span className="font-mono text-[9px] font-black uppercase tracking-[0.3em] text-red-400">NCDB · Most Wanted</span>
          <span className="font-mono text-[7px] text-zinc-600 uppercase tracking-[0.2em] ml-auto shrink-0">bounties live</span>
        </div>
        <div className="divide-y divide-white/[0.06]">
          {wanted.slice(0, 3).map((w, i) => (
            <div key={w.pc} data-testid={`wanted-${i}`} className="px-3.5 py-2.5 flex items-center gap-3">
              <span className="w-9 h-11 shrink-0 border border-red-900/50 bg-gradient-to-b from-[#16181d] to-[#0a0b0e] flex items-center justify-center relative">
                <span className="font-mono font-bold text-xs text-red-400/80">{w.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}</span>
                <span className="absolute inset-x-0 top-1/3 border-t border-white/10" />
                <span className="absolute inset-x-0 top-2/3 border-t border-white/10" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-mono text-[10px] font-bold text-zinc-100 uppercase tracking-[0.08em] truncate">{w.name}</span>
                <span className="block font-mono text-[8px] text-zinc-500 truncate">{w.reason}</span>
                <span className="block font-mono text-[9px] font-bold mt-0.5" style={{ color: accent }}>${w.bounty.toLocaleString()}{w.serious ? " · ARMED" : ""}</span>
              </span>
              <span className="font-mono text-[8px] text-red-500/60 shrink-0">#{i + 1}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* The anchored intel rail — every card the city wants to show you, in one column */
export function IntelRail({ world, accent, onRefresh, onContinue }) {
  const me = world?.me;
  const [leakOpen, setLeakOpen] = useState(false);
  const cards = [];
  if (!world) return null;

  if (me?.continue) cards.unshift(
    <button key="continue" data-testid="continue-card" onClick={onContinue}
      className={`${railCard} p-4 text-left transition-colors ${me.continue.online ? "border-emerald-500/50 hover:border-emerald-400/80" : "border-white/12 hover:border-white/30"}`}
      style={{ ...rise, boxShadow: me.continue.online ? "0 0 40px rgba(16,185,129,0.14)" : "none" }}>
      <p className={`${railLabel} flex items-center gap-2 ${me.continue.online ? "text-emerald-400" : "text-zinc-500"}`}>
        <Play className="w-3 h-3" /> Continue your story</p>
      <p className="font-bold uppercase tracking-wide text-zinc-50 mt-2 text-sm">{me.continue.character || "Your character"}</p>
      <p className="font-mono text-[9px] text-zinc-400 mt-0.5">{me.continue.server_name}</p>
      <p className={`font-mono text-[8px] mt-2 uppercase tracking-[0.2em] ${me.continue.online ? "text-emerald-500/80" : "text-zinc-600"}`}>
        {me.continue.online ? "world is live — jump back in →" : "world offline right now"}</p>
    </button>);

  if (me?.my_worlds?.length) cards.push(
    <div key="worlds" data-testid="my-worlds" className={`${railCard} border-white/12 p-4`} style={rise}>
      <p className={`${railLabel} text-zinc-500 flex items-center gap-2`}><Globe className="w-3 h-3" /> Your worlds — live</p>
      <div className="mt-2.5 flex flex-col gap-1.5">
        {me.my_worlds.map((w) => (
          <div key={w.server_id} data-testid={`my-world-${w.server_id}`} className="flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${w.online ? "bg-emerald-400 animate-pulse" : "bg-zinc-700"}`} />
            <span className="font-mono text-[9px] text-zinc-300 truncate flex-1">{w.name}</span>
            <span className="font-mono text-[8px] text-zinc-600 shrink-0">{w.online ? `${w.players} in city` : "offline"}</span>
          </div>
        ))}
      </div>
    </div>);

  if (me?.detective) cards.push(
    <div key="detective" data-testid="detective-rail" className={`${railCard} border-amber-500/40 p-4`} style={rise}>
      <p className={`${railLabel} text-amber-400 flex items-center gap-2`}><FileSearch className="w-3 h-3" /> Hall of Detectives</p>
      <p className="font-bold uppercase tracking-wide text-zinc-50 mt-2 text-sm">Rank #{me.detective.rank}</p>
      <p className="font-mono text-[9px] text-zinc-400 mt-0.5">{me.detective.cases} {me.detective.cases === 1 ? "case" : "cases"} closed · ${me.detective.bounties?.toLocaleString()} in bounties</p>
      <p className="font-mono text-[8px] text-zinc-600 mt-1.5 uppercase tracking-[0.2em]">the city remembers its sharpest minds</p>
    </div>);

  if (me?.mole) cards.push(
    <div key="mole" data-testid="mole-notice" className={`${railCard} border-amber-500/40 p-4`} style={{ ...rise, boxShadow: "0 0 40px rgba(232,185,62,0.12)" }}>
      <p className={`${railLabel} text-amber-400 flex items-center gap-2`}><EyeOff className="w-3 h-3" /> Eyes only</p>
      <p className="font-mono text-[10px] text-amber-100/90 leading-relaxed mt-2">You have been chosen. The department is watching.</p>
      <p className="font-mono text-[7px] text-zinc-600 mt-2 uppercase tracking-[0.2em]">this message does not exist for anyone else</p>
    </div>);
  else if (me?.prophecy) cards.push(
    <div key="prophecy" data-testid="prophecy-card" className={`${railCard} border-white/12 p-4`} style={rise}>
      <p className={`${railLabel} text-zinc-500 flex items-center gap-2`}><Newspaper className="w-3 h-3" /> The City Gazette · tonight</p>
      <p className="font-serif italic text-[13px] text-zinc-200 leading-snug mt-2">"{me.prophecy}"</p>
    </div>);

  if (me?.absence?.abandoned) cards.push(
    <div key="absence" data-testid="absence-card" className={`${railCard} border-emerald-900/50 p-4`} style={rise}>
      <p className={`${railLabel} text-emerald-700`}>{me.absence.days} days away — the city moved on</p>
      <p className="font-serif italic text-[12px] text-zinc-300 leading-snug mt-2">"{me.absence.message}"</p>
    </div>);

  if (me?.warrant) cards.push(
    <div key="poster" data-testid="my-wanted-poster" className={`${railCard} border-red-500/40 p-4`} style={{ ...rise, boxShadow: "0 0 40px rgba(239,68,68,0.1)" }}>
      <p className="font-mono text-[9px] font-black uppercase tracking-[0.5em] text-red-500 text-center border-y-2 border-red-500/50 py-1.5">Wanted</p>
      <p className="font-bold uppercase tracking-wide text-center text-zinc-50 mt-3 text-sm">{me.name}</p>
      <p className="font-mono text-[8px] text-zinc-400 text-center mt-1 uppercase tracking-[0.1em]">#{me.pc}</p>
      <p className="font-mono text-[8px] text-zinc-500 mt-3 leading-relaxed text-center">{me.warrant.reason}</p>
      <p className="font-mono text-[10px] font-bold text-center mt-3 text-amber-400">REWARD ${me.warrant.bounty.toLocaleString()}</p>
      <p className="font-mono text-[7px] text-zinc-600 text-center mt-2 uppercase tracking-[0.2em]">turn yourself in · bornwood central</p>
    </div>);

  const cc = world.cold_cases?.[0];
  if (cc) cards.push(
    <div key="cold" data-testid="cold-case" className={`${railCard} border-amber-900/50 p-4`} style={rise}>
      <p className={`${railLabel} text-amber-600 flex items-center gap-2`}><FileWarning className="w-3 h-3" /> Cold case · {cc.days_open}d open</p>
      <p className="font-mono text-[9px] font-bold text-zinc-300 mt-2">{cc.docket}</p>
      <p className="font-mono text-[8px] text-zinc-500 leading-relaxed mt-1">{Array.isArray(cc.charges) ? (cc.charges.map((c) => c?.title || c).filter(Boolean).join(" · ") || "charges sealed") : (cc.charges || "charges sealed")}</p>
      <p className="font-mono text-[7px] text-zinc-600 mt-2 uppercase tracking-[0.15em]">someone in this city knows something</p>
    </div>);

  if (world.leak) cards.push(
    <button key="leak" data-testid="leak-tab" onClick={() => setLeakOpen(true)}
      className={`${railCard} border-red-900/60 p-4 text-left hover:border-red-500/50 transition-colors`} style={rise}>
      <p className={`${railLabel} text-red-400 flex items-center gap-2`}><Skull className="w-3 h-3" /> Leaked file · {world.leak.docket}</p>
      <p className="font-mono text-[8px] text-zinc-500 mt-2 leading-relaxed">A classified NCDB file is circulating. Redacted. The whole city is reading between the black bars.</p>
      <p className="font-mono text-[8px] text-red-400/80 mt-2 uppercase tracking-[0.2em]">Open document →</p>
    </button>);

  if (world.broadcast) cards.push(
    <div key="bcast" data-testid="faction-broadcast" className={`${railCard} border-sky-500/30 p-4`} style={rise}>
      <p className={`${railLabel} text-sky-400 flex items-center gap-2`}><Radio className="w-3 h-3 animate-pulse" /> Incoming transmission</p>
      <div className="flex items-end gap-0.5 h-4 mt-2">
        {Array.from({ length: 24 }, (_, i) => (
          <span key={i} className="flex-1 bg-sky-400/70 rounded-sm origin-bottom"
            style={{ animation: `phEq ${0.5 + (i % 5) * 0.11}s ease-in-out ${i * 0.05}s infinite`, height: "100%" }} />
        ))}
      </div>
      <p className="font-mono text-[10px] text-zinc-200 leading-relaxed mt-2">{world.broadcast.message}</p>
      <p className="font-mono text-[7px] text-zinc-600 mt-1.5 uppercase tracking-[0.2em]">— {world.broadcast.by}</p>
      <style>{`@keyframes phEq{0%,100%{transform:scaleY(0.25)}50%{transform:scaleY(1)}}`}</style>
    </div>);

  if (!cards.length && !world.leak) return null;
  return (
    <>
      <div data-testid="intel-rail" className="absolute left-6 top-[76px] z-10 w-72 flex flex-col gap-3 max-h-[54vh] overflow-y-auto pointer-events-none" style={{ scrollbarWidth: "thin" }}>
        {cards}
      </div>
      {leakOpen && world.leak && (
        <div data-testid="leak-doc" className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setLeakOpen(false)}>
          <div className="w-full max-w-md bg-[#0d0c0a] border border-red-900/50 p-6 rotate-[-0.5deg] pointer-events-auto" style={{ ...rise, boxShadow: "0 30px 80px rgba(0,0,0,0.8)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-[9px] font-black uppercase tracking-[0.4em] text-red-500">Classified — leaked</p>
              <button data-testid="leak-close" onClick={() => setLeakOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="font-mono text-[10px] leading-[1.9] text-zinc-300 whitespace-pre-wrap">
              {world.leak.lines.map((l, i) => <p key={i}>{l}</p>)}
            </div>
            <p className="font-mono text-[8px] text-zinc-600 mt-4 uppercase tracking-[0.2em]">circulates for 24h · the city will work out who this is</p>
          </div>
        </div>
      )}
    </>
  );
}

/* Dead drop — the briefcase glows in the backdrop; the button sits on the glow */
export function DeadDrop({ world, onOpened }) {
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!world?.me?.dead_drops) return null;
  const openIt = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const list = await api.get("/nc/menu/deaddrops", { params: { server_id: world.server.id } });
      const pending = (list.data.drops || []).find((d) => !d.opened);
      if (!pending) { toast.info("The briefcase is empty — old drop"); onOpened?.(); return; }
      const r = await api.post(`/nc/menu/deaddrop/${pending.id}/open`);
      setRevealed(r.data.body);
    } catch { toast.error("The briefcase isn't meant for you"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="deaddrop-briefcase" onClick={openIt}
        className="absolute z-10 pointer-events-auto -translate-x-1/2 -translate-y-1/2 group"
        style={{ left: `${DEAD_DROP_POS.x * 100}%`, top: `${DEAD_DROP_POS.y * 100}%` }}>
        <Briefcase className="w-9 h-9 text-amber-400 drop-shadow-[0_0_12px_rgba(232,185,62,0.8)] group-hover:scale-110 transition-transform animate-pulse" />
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 text-black font-mono text-[9px] font-bold flex items-center justify-center">{world.me.dead_drops}</span>
      </button>
      {revealed && (
        <div data-testid="deaddrop-reveal" className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setRevealed(null); onOpened?.(); }}>
          <div className="w-full max-w-sm border border-amber-500/40 bg-[#0a0a08] p-6 pointer-events-auto" style={rise} onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[8px] uppercase tracking-[0.35em] text-amber-500 flex items-center gap-2"><Briefcase className="w-3 h-3" /> Dead drop · no sender</p>
            <p className="font-mono text-[11px] text-zinc-200 leading-relaxed mt-3 whitespace-pre-wrap">{revealed}</p>
            <p className="font-mono text-[7px] text-zinc-600 mt-3 uppercase tracking-[0.2em]">no name. no trace. burn after reading.</p>
            <button data-testid="deaddrop-close" onClick={() => { setRevealed(null); onOpened?.(); }}
              className="mt-4 w-full py-2.5 font-mono text-[9px] uppercase tracking-[0.3em] font-bold text-black" style={{ backgroundColor: "#e8b93e" }}>Burn it</button>
          </div>
        </div>
      )}
    </>
  );
}

/* Breaking news — the in-universe channel reading the real activity feed */
export function BreakingNews({ items }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!items || items.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items]);
  if (!items?.length) return null;
  return (
    <div data-testid="breaking-news" className="flex items-center gap-3 border border-white/12 bg-[#04060a]/95 backdrop-blur-md px-4 py-2.5 max-w-2xl pointer-events-none" style={rise}>
      <span className="font-mono text-[8px] font-black uppercase tracking-[0.25em] text-red-500 shrink-0 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
      </span>
      <p className="font-mono text-[9px] text-zinc-300 uppercase tracking-[0.1em] truncate">{items[i % items.length]}</p>
    </div>
  );
}
